import React, { useState } from "react";
import Section from "./layouts/Section";
import { submitLead } from "../utils/leadSubmit";
import { track, EVENTS, trackPhoneCall } from "../utils/analytics";

/**
 * Business & Corporate Accounts landing page (Growth Platform — Slice S4).
 *
 * Dedicated B2B experience (spec §11) for schools, realtors, contractors,
 * restaurants, medical offices, delivery businesses and local companies.
 * Frontend-only lead-generation for now; approved-account backend deferred
 * to the MERN phase. Submits via the shared EmailJS pipeline.
 */

const NAVY = "#18305d";
const ACCENT = "#ffaa19";

const BENEFITS = [
  { icon: "fas fa-tags", title: "Corporate Pricing", text: "Volume discounts on repairs across your device fleet." },
  { icon: "fas fa-bolt", title: "Priority Service", text: "Front-of-line turnaround to minimize downtime." },
  { icon: "fas fa-mobile-alt", title: "Multi-Device Repairs", text: "One point of contact for phones, tablets & laptops." },
  { icon: "fas fa-clipboard-list", title: "Fleet Management", text: "Track and manage all your company devices in one place." },
  { icon: "fas fa-file-invoice-dollar", title: "Monthly Billing", text: "Consolidated invoicing options for approved accounts." },
  { icon: "fas fa-headset", title: "Dedicated Support", text: "A direct line to our team for your business." },
];

const SEGMENTS = [
  "School / District",
  "Real Estate",
  "Contractor / Trades",
  "Restaurant",
  "Medical Office",
  "Delivery / Logistics",
  "Other Business",
];

function BusinessAccounts() {
  const [form, setForm] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    segment: "",
    devices: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [done, setDone] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const canSubmit = form.company.trim() && form.name.trim() && form.email.trim();

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    const details = [
      "New Business Account Request",
      "",
      `Company: ${form.company}`,
      `Contact: ${form.name}`,
      `Business Type: ${form.segment || "Not specified"}`,
      `Approx. Devices: ${form.devices || "Not specified"}`,
      "",
      `Message: ${form.message || "None"}`,
    ].join("\n");

    submitLead({
      name: form.name,
      email: form.email,
      phone: form.phone,
      device: `Business: ${form.company}`,
      subject: `Business Account Request: ${form.company}`,
      message: details,
    })
      .then(() => {
        track(EVENTS.BUSINESS_ACCOUNT_LEAD, {
          segment: form.segment || "unspecified",
        });
        setDone(true);
        setIsSubmitting(false);
      })
      .catch((error) => {
        setStatus({
          type: "error",
          message:
            "Something went wrong. Please try again or call us at (215) 939-3933.",
        });
        setIsSubmitting(false);
        // eslint-disable-next-line no-console
        console.log("FAILED...", error?.text);
      });
  };

  return (
    <Section title={"Business Accounts"}>
      <section className="section page-content">
        <div className="container">
          <div className="row justify-content-center mb-40">
            <div className="col-lg-9 text-center">
              <h2 className="text-dark mb-15">
                Device repair, built for your business.
              </h2>
              <p className="text-muted" style={{ fontSize: "17px" }}>
                Keep your team's phones, tablets and laptops running. Cell
                Station offers corporate pricing, priority turnaround and
                dedicated support for local businesses of every size.
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="row mb-30">
            {BENEFITS.map((b) => (
              <div className="col-md-6 col-lg-4 mb-25" key={b.title}>
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "14px",
                    padding: "26px",
                    height: "100%",
                    boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
                  }}
                >
                  <span
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      background: "#fff9ef",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "14px",
                    }}
                  >
                    <i
                      className={b.icon}
                      style={{ color: ACCENT, fontSize: "20px" }}
                    />
                  </span>
                  <h5 className="text-dark mb-8" style={{ fontWeight: 700 }}>
                    {b.title}
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                    {b.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Lead form */}
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {!done ? (
                <div
                  style={{
                    background: NAVY,
                    borderRadius: "16px",
                    padding: "34px",
                    boxShadow: "0 10px 40px rgba(24,48,93,0.25)",
                  }}
                >
                  <h4 className="text-white mb-10">Request a business account</h4>
                  <p style={{ color: "rgba(255,255,255,0.8)" }} className="mb-25">
                    Tell us about your business and we'll follow up with a
                    tailored plan.
                  </p>

                  {status.message && (
                    <div
                      style={{
                        padding: "12px 16px",
                        borderRadius: "8px",
                        marginBottom: "18px",
                        background: "#f8d7da",
                        color: "#721c24",
                      }}
                    >
                      <i className="fas fa-exclamation-circle mr-2" />
                      {status.message}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="form-group mb-20 col-md-6">
                        <label className="mb-2 font-w-600 text-white">
                          Company *
                        </label>
                        <input
                          className="form-control py-3 px-3"
                          type="text"
                          required
                          placeholder="Company name"
                          value={form.company}
                          onChange={(e) => update({ company: e.target.value })}
                        />
                      </div>
                      <div className="form-group mb-20 col-md-6">
                        <label className="mb-2 font-w-600 text-white">
                          Business Type
                        </label>
                        <select
                          className="form-control py-3 px-3"
                          value={form.segment}
                          onChange={(e) => update({ segment: e.target.value })}
                        >
                          <option value="">Select...</option>
                          {SEGMENTS.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group mb-20 col-md-6">
                        <label className="mb-2 font-w-600 text-white">
                          Contact Name *
                        </label>
                        <input
                          className="form-control py-3 px-3"
                          type="text"
                          required
                          placeholder="Your name"
                          value={form.name}
                          onChange={(e) => update({ name: e.target.value })}
                        />
                      </div>
                      <div className="form-group mb-20 col-md-6">
                        <label className="mb-2 font-w-600 text-white">
                          Email *
                        </label>
                        <input
                          className="form-control py-3 px-3"
                          type="email"
                          required
                          placeholder="you@company.com"
                          value={form.email}
                          onChange={(e) => update({ email: e.target.value })}
                        />
                      </div>
                      <div className="form-group mb-20 col-md-6">
                        <label className="mb-2 font-w-600 text-white">
                          Phone
                        </label>
                        <input
                          className="form-control py-3 px-3"
                          type="tel"
                          placeholder="(215) 555-1234"
                          value={form.phone}
                          onChange={(e) => update({ phone: e.target.value })}
                        />
                      </div>
                      <div className="form-group mb-20 col-md-6">
                        <label className="mb-2 font-w-600 text-white">
                          Approx. # of Devices
                        </label>
                        <input
                          className="form-control py-3 px-3"
                          type="text"
                          placeholder="e.g. 25"
                          value={form.devices}
                          onChange={(e) => update({ devices: e.target.value })}
                        />
                      </div>
                      <div className="form-group mb-20 col-md-12">
                        <label className="mb-2 font-w-600 text-white">
                          Anything else?
                        </label>
                        <textarea
                          className="form-control p-3"
                          rows="3"
                          placeholder="Tell us about your needs..."
                          value={form.message}
                          onChange={(e) => update({ message: e.target.value })}
                        />
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      type="submit"
                      disabled={isSubmitting || !canSubmit}
                      style={{ padding: "13px 34px" }}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-briefcase mr-2" />
                          Request Business Account
                        </>
                      )}
                    </button>
                  </form>
                </div>
              ) : (
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "16px",
                    padding: "40px 30px",
                    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "50%",
                      background: "#d4edda",
                      color: "#155724",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 18px",
                      fontSize: "30px",
                    }}
                  >
                    <i className="fas fa-check" />
                  </div>
                  <h4 className="text-dark mb-10">Request received!</h4>
                  <p className="text-muted mb-20" style={{ maxWidth: "460px", margin: "0 auto 20px" }}>
                    Thanks {form.name.split(" ")[0] || "there"}! Our team will
                    reach out to {form.company || "your business"} shortly to set
                    up your account.
                  </p>
                  <a
                    href="tel:+12159393933"
                    className="btn btn-primary"
                    onClick={() => trackPhoneCall("business_success")}
                  >
                    <i className="fas fa-phone-alt mr-2" />
                    Call (215) 939-3933
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </Section>
  );
}

export default BusinessAccounts;
